
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ProfileData } from '@/hooks/useProfiles';

export const useProfileManagement = (
  profiles: ProfileData[],
  setProfiles: React.Dispatch<React.SetStateAction<ProfileData[]>>
) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  const deleteUser = async (userId: string) => {
    if (isDeleting) return false;
    
    try {
      setIsDeleting(true);
      
      // First try to delete the user's auth record using admin API
      const { error: authError } = await supabase.auth.admin.deleteUser(userId);
      
      if (authError) {
        console.error('Error deleting auth user:', authError);
        
        // If we get a permission error, we can try deleting just the profile
        if (authError.message?.includes('permission denied')) {
          const { error: profileError } = await supabase
            .from('profiles')
            .delete()
            .eq('id', userId);
            
          if (profileError) {
            throw profileError;
          }
          
          // Update the local state even if we couldn't delete the auth record
          setProfiles(profiles.filter(p => p.id !== userId));
          
          toast({
            title: "Profile Removed",
            description: "User profile removed. Note: The authentication record may require additional cleanup in the Supabase dashboard.",
          });
          
          return true;
        }
        
        throw authError;
      }
      
      // If auth user deleted successfully, update local state
      setProfiles(profiles.filter(p => p.id !== userId));
      
      toast({
        title: "User Deleted",
        description: "User has been successfully deleted.",
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      
      toast({
        title: "Deletion Failed",
        description: error.message || "Failed to delete user. Please try again or contact support.",
        variant: "destructive",
      });
      
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    deleteUser,
    isDeleting
  };
};
